import type { CatalogueIndustrialQueryPlan, IndustrialSearchProduct } from "./catalogue-industrial-search.js";

const LOSS = /(?:loss|dissipat|consumption|braking resistor)/i;

export function matchesCatalogueIndustrialEvidence(product: IndustrialSearchProduct, plan: CatalogueIndustrialQueryPlan): boolean {
  const entries = Object.entries(product.specifications || {}).map(([key, value]) => `${key}: ${String(value)}`);
  const identity = [product.title, product.family, ...entries.filter((entry) => /^(?:type of module|product type|device type):/i.test(entry))].filter(Boolean).join("\n").toLowerCase();
  const text = [identity, product.description, product.mpn, product.manufacturer, product.manufacturerId, ...entries].filter(Boolean).join("\n").toLowerCase();
  const power = [product.title, product.family, ...(product.description || "").split(/[;\n]/).filter((part) => !LOSS.test(part)), ...entries.filter((entry) => !LOSS.test(entry))].filter(Boolean).join("\n").toLowerCase();
  const output = catalogueRoleEvidence(text, "output");
  const coil = catalogueRoleEvidence(text, "coil");
  const current = catalogueRoleEvidence(text, "current");
  if (new RegExp(plan.excludeIdentity, "i").test(identity)) return false;
  return plan.requirements.every(({ scope, pattern }) => new RegExp(pattern, "i").test({ identity, text, power, output, coil, current }[scope]));
}

/** Keep electrical values attached to their stated role, excluding adjacent ratings. */
export function catalogueRoleEvidence(text: string, role: "output" | "coil" | "current"): string {
  if (role === "output") text = text.replace(/relay(?:[ -]+status)?[ -]+output/g, "relay status signal");
  const pattern = role === "output" ? /\b(?:output|secondary)\b[^;\n]*/g
    : role === "coil" ? /\b(?:coil|control[ -]+(?:circuit[ -]+)?voltage)\b[^;,\n]*/g
      : /\brated[ -]+(?:operational[ -]+)?current\b[^;,\n]*/g;
  const stop = role === "output" ? /\b(?:input|primary|auxiliary|coil|relay)\b/
    : role === "coil" ? /\b(?:auxiliary|input|output|rated|supply)\b/
      : /\b(?:auxiliary|coil|input|output|supply)\b/;
  const segments = [...text.matchAll(pattern)].map(([segment]) => segment.split(stop)[0]);
  if (role === "coil") segments.push(...[...text.matchAll(/(?:^|[^a-z0-9])\d+(?:[.,]\d+)?[ -]*v[ -]*(?:ac|dc)?[ -]+coil\b/g)].map(([segment]) => segment));
  if (segments.length) return segments.join("\n");
  if (role === "coil") return "";
  return text.replace(role === "output" ? /\b(?:input|primary|auxiliary|coil|relay)\b[^;\n]*/g
    : /\b(?:auxiliary|coil|input|output|supply)\b[^;,\n]*/g, " ");
}
